import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ConsultationContactForm from '@/components/ConsultationContactForm';
import { Button } from '@/components/ui/button';
import { Cloud } from 'lucide-react';
import { Link } from 'react-router-dom';

const CloudSolutions: React.FC = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      
      <section className="bg-itmgmt-dark-blue text-white py-16 md:py-24">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="heading-xl mb-6">
                Cloud Solutions
              </h1>
              <p className="text-xl mb-8 text-gray-300">
                Leverage the power of cloud technology for increased flexibility, scalability, 
                and productivity with our managed cloud services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="button-primary">
                  <Link to="/book?service=cloud">Book a Consultation</Link>
                </Button>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="bg-itmgmt-medium-blue bg-opacity-20 p-8 rounded-full">
                <Cloud size={120} className="text-itmgmt-bright-blue" />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="section-padding bg-white">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="heading-lg text-itmgmt-dark-blue mb-6">Cloud Solutions We Offer</h2>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're looking to migrate entirely to the cloud or implement a hybrid approach, 
                our cloud solutions provide the scalability, security, and accessibility your business needs.
              </p>
              
              <div className="space-y-6">
                <div className="bg-itmgmt-light-gray p-6 rounded-lg">
                  <h3 className="font-bold text-itmgmt-dark-blue text-xl mb-3">Our Cloud Services</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Cloud Email & Productivity Suite Management</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Email Migrations & License Optimization</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Cloud Backup Solutions</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Hosted VoIP Phone Systems</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Hybrid Cloud Server Environments</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Thin Client Implementation</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div>
              <h2 className="heading-lg text-itmgmt-dark-blue mb-6">Benefits of Cloud Computing</h2>
              <div className="space-y-6">
                <div className="flex">
                  <div className="bg-itmgmt-medium-blue rounded-full h-10 w-10 flex items-center justify-center shrink-0 mr-4">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-itmgmt-dark-blue text-lg mb-2">Cost Efficiency</h3>
                    <p className="text-gray-600">
                      Reduce capital expenses on hardware and shift to predictable operational costs with subscription-based services.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="bg-itmgmt-medium-blue rounded-full h-10 w-10 flex items-center justify-center shrink-0 mr-4">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-itmgmt-dark-blue text-lg mb-2">Flexibility & Scalability</h3>
                    <p className="text-gray-600">
                      Easily scale resources up or down based on demand without major infrastructure investments.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="bg-itmgmt-medium-blue rounded-full h-10 w-10 flex items-center justify-center shrink-0 mr-4">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-itmgmt-dark-blue text-lg mb-2">Business Continuity</h3>
                    <p className="text-gray-600">
                      Ensure data protection and disaster recovery with redundant cloud systems that minimize downtime.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="bg-itmgmt-medium-blue rounded-full h-10 w-10 flex items-center justify-center shrink-0 mr-4">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-itmgmt-dark-blue text-lg mb-2">Remote Collaboration</h3>
                    <p className="text-gray-600">
                      Enable seamless teamwork with anywhere, anytime access to applications and data from any device.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="section-padding bg-itmgmt-light-gray">
        <div className="container-wide">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-itmgmt-dark-blue mb-4">Our Cloud Migration Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We take a methodical approach to cloud migration to ensure minimal disruption and maximum benefit.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-itmgmt-dark-blue text-xl mb-4">1. Assess & Plan</h3>
              <p className="text-gray-600 mb-4">
                We evaluate your current IT environment and business needs to develop a strategic migration plan.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Application & Workload Assessment</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Cloud Readiness Evaluation</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>ROI & TCO Analysis</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-itmgmt-dark-blue text-xl mb-4">2. Migrate & Implement</h3>
              <p className="text-gray-600 mb-4">
                We execute the migration with careful planning to minimize business disruption.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Data Migration</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Application Configuration</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Security Implementation</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-itmgmt-dark-blue text-xl mb-4">3. Optimize & Manage</h3>
              <p className="text-gray-600 mb-4">
                We provide ongoing management to ensure optimal performance and cost-efficiency.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Performance Monitoring</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Cost Optimization</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Continuous Improvement</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <Button asChild className="button-primary">
              <Link to="/book?service=cloud">Explore Cloud Solutions</Link>
            </Button>
          </div>
        </div>
      </section>
      
      <ConsultationContactForm />
      <Footer />
    </div>
  );
};

export default CloudSolutions;
